<template>
  <div :id="`${source}_routing_funnel_grid`">
    <CustomContainer padding="p-0">
      <div class="flex flex-row items-center justify-between px-10 py-5">
        <InfoStat
          v-for="stat in routingStats"
          :key="`${stat.id}_stat`"
          :source="`${source}_routing_funnel_grid`"
          :data="stat"
        />
      </div>
    </CustomContainer>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted } from "vue";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import CustomContainer from "@/src/components/CustomContainer.vue";
import InfoStat from "@/src/components/info_components/InfoStat.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const routingStatsConfig = [
  {
    id: "total_responses",
    title: "Total responses",
    dataKey: "total_response",
    description:
      "Total number of form submissions within your selected time range.",
  },
  {
    id: "total_responses_with_booking",
    title: "Total responses with booking",
    dataKey: "meeting_booked",
    description:
      "Total number of form submissions that resulted in booked meetings.",
  },
  {
    id: "total_responses_with_no_booking",
    title: "Total responses with no booking",
    dataKey: "meeting_not_booked",
    description:
      "Total number of form submissions that did NOT result in booked meetings.",
  },
];

const routingStats = ref(
  routingStatsConfig.map((config) => ({
    id: config.id,
    title: config.title,
    description: config.description,
    value: "0",
    isLoading: true,
  })),
);

const fetchRoutingFormStats = async () => {
  routingStats.value.forEach((stat) => {
    stat.isLoading = true;
  });

  try {
    const params = {
      start_time: range.value?.start,
      end_time: range.value?.end,
      user_id: userIds.value,
      team_id: teamIds.value,
      routing_form_id: filters.value?.routing_form?.id,
    };

    const response = await AnalyticService.V2.fetchRoutingFormFunnel(params);

    if (response?.success && response?.data) {
      const data = response?.data?.data;

      routingStatsConfig.forEach((config) => {
        const statItem = routingStats.value.find(
          (stat) => stat.id === config.id,
        );
        if (statItem) {
          statItem.value = (data[config.dataKey] || 0).toString();
          statItem.isLoading = false;
        }
      });
    }
  } catch (error) {
    console.error("Error fetching routing form stats:", error);

    routingStats.value.forEach((stat) => {
      stat.value = "0";
      stat.isLoading = false;
    });
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) {
    fetchRoutingFormStats();
  }
});

useAnalyticsRefresh(fetchRoutingFormStats, [range, userIds, teamIds]);

onMounted(() => {
  fetchRoutingFormStats();
});
</script>
